/**
 * Webhooks (FR-52, system admin): signed POSTs of every change, ids and field names only.
 * The signing secret is shown once, at creation or rotation.
 */
import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Webhook, WebhookCreated, WebhookDelivery } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { useRefresh, wsKey } from "../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../ui";
import { HistoryLink } from "../activity";
import { askText, confirmAction } from "../confirm";

function Secret({ value, onDone }: { value: string; onDone: () => void }) {
  return (
    <div className="secret" role="status">
      <strong>Copy this signing secret now; it will not be shown again.</strong>
      <code>{value}</code>
      <span className="actions">
        <button onClick={() => void navigator.clipboard.writeText(value)}>Copy</button>
        <button className="secondary" onClick={onDone}>Done</button>
      </span>
    </div>
  );
}

const patterns = (s: string) => s.split(/[\s,]+/).filter(Boolean);

function Deliveries({ slug, hook }: { slug: string; hook: Webhook }) {
  const refresh = useRefresh(slug);
  const list = useQuery({ queryKey: wsKey(slug, `deliveries-${hook.id}`), queryFn: async () => z.array(WebhookDelivery).parse(await get(`/v1/ws/${slug}/webhooks/${hook.id}/deliveries`, { limit: 20 })) });
  const redeliver = useMutation({ mutationFn: (id: number) => checkedWrite("POST", `/v1/ws/${slug}/webhooks/${hook.id}/deliveries/${id}/redeliver`), onSuccess: refresh });
  const state = (d: WebhookDelivery) => (d.deliveredAt ? "delivered" : d.deadAt ? "dead" : "pending");
  return (
    <>
      <ErrorBox error={list.error ?? redeliver.error} />
      {list.data?.length === 0 && <p className="muted small">No deliveries yet.</p>}
      {(list.data?.length ?? 0) > 0 && (
        <table className="grid">
          <thead><tr><th>Event</th><th>State</th><th>Attempts</th><th>Last status</th><th>When</th><th /></tr></thead>
          <tbody>
            {list.data?.map((d) => (
              <tr key={d.id}>
                <td><code>{d.eventType}</code> <span className="muted small">activity #{d.activityId}</span></td>
                <td><StatusBadge status={state(d)} /></td>
                <td>{d.attempts}</td>
                <td>{d.lastStatus ?? ""} <span className="muted small">{d.lastError ?? ""}</span></td>
                <td><When at={d.deliveredAt ?? d.deadAt ?? d.nextAttemptAt} /></td>
                <td className="actions">{state(d) !== "pending" && <button className="link" onClick={() => redeliver.mutate(d.id)}>Redeliver</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function HookCard({ slug, hook }: { slug: string; hook: Webhook }) {
  const refresh = useRefresh(slug);
  const base = `/v1/ws/${slug}/webhooks/${hook.id}`;
  const [secret, setSecret] = useState<string>();
  const [adding, setAdding] = useState(false);
  const [show, setShow] = useState(false);
  const update = useMutation({ mutationFn: (body: Record<string, unknown>) => checkedWrite("PATCH", base, body), onSuccess: refresh });
  // No dry run first: it would generate (and throw away) a secret.
  const rotate = useMutation({
    mutationFn: async () => WebhookCreated.partial({ secret: true }).parse(await api("PATCH", base, { body: { rotateSecret: true } })),
    onSuccess: (h) => (setSecret(h.secret), refresh()),
  });
  const remove = useMutation({ mutationFn: () => checkedWrite("DELETE", base), onSuccess: refresh });
  return (
    <Panel
      title={hook.name}
      actions={
        <span className="actions">
          <StatusBadge status={hook.active ? "active" : "inactive"} />
          <RowMenu
            label={`Actions for ${hook.name}`}
            items={[
              { label: hook.active ? "Pause" : "Resume", onSelect: () => update.mutate({ active: !hook.active }) },
              { label: "Change URL…", onSelect: () => void askText({ title: `Change the URL of '${hook.name}'`, action: "Save", input: { label: "URL", value: hook.url, type: "url" } }).then((u) => u && update.mutate({ url: u })) },
              { label: "Events…", onSelect: () => void askText({ title: `Events sent to '${hook.name}'`, action: "Save", input: { label: "Events", value: hook.eventFilter.join(" "), help: "Patterns separated by spaces, e.g. work_object.*. Empty for every event.", required: false } }).then((f) => f !== undefined && update.mutate({ eventFilter: patterns(f) })) },
              { label: "Rotate secret…", onSelect: () => void confirmAction({ title: `Rotate the signing secret of '${hook.name}'?`, body: "The old secret stops working at once. The new one is shown once, and the receiver must switch to it.", action: "Rotate", check: () => api("PATCH", base, { body: { rotateSecret: true }, dryRun: true }) }).then((ok) => ok && rotate.mutate()) },
              { label: "Delete…", danger: true, onSelect: () => void confirmAction({ title: `Delete webhook '${hook.name}'?`, body: `No more events are sent to ${hook.url}.`, action: "Delete", check: () => api("DELETE", base, { dryRun: true }) }).then((ok) => ok && remove.mutate()) },
            ]}
          />
        </span>
      }
    >
      {secret && <Secret value={secret} onDone={() => setSecret(undefined)} />}
      <p><code>{hook.url}</code></p>
      <p className="muted small">Events: {hook.eventFilter.length ? hook.eventFilter.join(", ") : "all"}</p>
      <ErrorBox error={update.error ?? rotate.error ?? remove.error} />
      <button className="link" aria-expanded={show} onClick={() => setShow(!show)}>{show ? "Hide deliveries" : "Show deliveries"}</button>
      {show && <Deliveries slug={slug} hook={hook} />}
    </Panel>
  );
}

export function WebhooksPage() {
  const { slug = "" } = useParams();
  const refresh = useRefresh(slug);
  const list = useQuery({ queryKey: wsKey(slug, "webhooks"), queryFn: async () => z.array(Webhook).parse(await get(`/v1/ws/${slug}/webhooks`)) });
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState("");
  const [secret, setSecret] = useState<string>();
  const [adding, setAdding] = useState(false);
  const create = useMutation({
    mutationFn: async () => {
      const body = { name, url, eventFilter: patterns(events) };
      await api("POST", `/v1/ws/${slug}/webhooks`, { body, dryRun: true });
      return WebhookCreated.parse(await api("POST", `/v1/ws/${slug}/webhooks`, { body }));
    },
    onSuccess: (h) => (setSecret(h.secret), setName(""), setUrl(""), setEvents(""), setAdding(false), refresh()),
  });
  return (
    <>
      <PageHeader title="Webhooks" description="Each change is sent to the receiver's URL, signed with its secret. Payloads carry ids and field names only." actions={<><HistoryLink slug={slug} kind="webhooks" /><button onClick={() => setAdding(true)}>New webhook</button></>} />
      {secret && <Secret value={secret} onDone={() => setSecret(undefined)} />}
      <SidePanel title="New webhook" open={adding} onClose={() => setAdding(false)} onSubmit={() => create.mutate()} submitLabel="Create webhook" busy={create.isPending} error={create.error}>
        <Field label="Name"><input required value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Audit sink" /></Field>
        <Field label="URL" help="Where changes are POSTed. Must be HTTPS in production."><input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" /></Field>
        <Field label="Events" help="Patterns separated by spaces, e.g. work_object.*. Leave empty for every event."><input value={events} onChange={(e) => setEvents(e.target.value)} /></Field>
      </SidePanel>
      <ErrorBox error={list.error} />
      {list.data?.length === 0 && <EmptyState title="No webhooks yet" body="A webhook sends each change in this workspace to a URL you choose, signed with a secret so the receiver can trust it." action={<button onClick={() => setAdding(true)}>New webhook</button>} />}
      {list.data?.map((h) => <HookCard key={h.id} slug={slug} hook={h} />)}
    </>
  );
}
